import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { User, LogOut, ChevronDown } from 'lucide-react';

function UserMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const menuRef = useRef(null);

  // Debug logs
  console.log('UserMenu renderizado, user:', user);
  console.log('UserMenu isOpen:', isOpen);

  // Fechar menu quando clicar fora
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleMenu = () => {
    console.log('Toggle menu clicked, current isOpen:', isOpen);
    setIsOpen(!isOpen);
  };

  const handleProfileClick = () => {
    try {
      navigate('/profile');
      setIsOpen(false);
    } catch (error) {
      console.error('Erro ao navegar para perfil:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Erro no logout:', error);
      // Fallback: redirecionar mesmo com erro
      navigate('/login');
    }
  };

  const getUserInitials = () => {
    try {
      if (user?.fullName) {
        return user.fullName
          .split(' ')
          .map(name => name.charAt(0))
          .join('')
          .toUpperCase()
          .slice(0, 2);
      }
      return user?.username?.charAt(0).toUpperCase() || 'U';
    } catch (error) {
      console.error('Erro ao gerar iniciais:', error);
      return 'U';
    }
  };

  const renderAvatar = () => {
    if (user?.avatar_base64) {
      return (
        <img 
          src={user.avatar_base64} 
          alt="Avatar do usuário"
          className="w-8 h-8 rounded-full object-cover"
        />
      );
    }
    
    return (
      <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center text-white font-medium text-sm">
        {getUserInitials()}
      </div>
    );
  };

  const getUserDisplayName = () => {
    try {
      return user?.fullName || user?.username || 'Usuário';
    } catch (error) {
      console.error('Erro ao obter nome do usuário:', error);
      return 'Usuário';
    }
  };

  const getUserRole = () => {
    try {
      if (user?.role === 'admin') return 'Administrador';
      if (user?.role === 'manager') return 'Gerente';
      if (user?.role === 'viewer') return 'Visualizador';
      if (user?.role === 'common_user') return 'Usuário Comum';
      return 'Usuário';
    } catch (error) {
      console.error('Erro ao obter role do usuário:', error);
      return 'Usuário';
    }
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={toggleMenu}
        className="flex items-center space-x-2 p-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors duration-200"
      >
        {renderAvatar()}
        <span className="hidden md:block text-sm font-medium">
          {getUserDisplayName()}
        </span>
        <ChevronDown 
          className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-50">
          {/* User Info */}
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-sm font-medium text-gray-900">
              {getUserDisplayName()}
            </p>
            <p className="text-xs text-gray-500 capitalize">
              {getUserRole()}
            </p>
          </div>

          {/* Menu Items */}
          <div className="py-1">
            <button
              onClick={handleProfileClick}
              className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors duration-150"
            >
              <User className="h-4 w-4 mr-3 text-gray-400" />
              Meu Perfil
            </button>
            
            <button
              onClick={handleLogout}
              className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors duration-150"
            >
              <LogOut className="h-4 w-4 mr-3 text-red-400" />
              Sair da Aplicação
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserMenu;
